import { useCallback, useMemo } from "react";

import { AssociatedCampaignAsset } from "../../store/apis/briefingApi";
import { components } from "../../types/api";
import {
  DestinationDraft,
  createDestinationDraft,
} from "./AssetDestinationsEditor";

export const toUtmToken = (value: string) =>
  encodeURIComponent(value.trim().toLowerCase().replace(/\s+/g, "_"));

/** Builds the destination rows from an asset, supporting the legacy single URL. */
export const toDestinationDrafts = (
  asset: AssociatedCampaignAsset | null
): DestinationDraft[] => {
  if (!asset) return [];

  if (Array.isArray(asset.destinations) && asset.destinations.length > 0) {
    return asset.destinations.map((destination) =>
      createDestinationDraft({
        id: destination.id ?? undefined,
        label: destination.label ?? "",
        url: destination.url ?? "",
        utm: destination.utm ?? "",
        isAutoUtm: destination.isAutoUtm ?? true,
      })
    );
  }

  if (asset.destinationUrl || asset.utm) {
    return [
      createDestinationDraft({
        url: asset.destinationUrl ?? "",
        utm: asset.utm ?? "",
        isAutoUtm: asset.isAutoUtm ?? !asset.utm,
      }),
    ];
  }

  return [];
};

/** Link maps may come as an object, a JSON string or a single bare link. */
export const toLinkRecord = (value: unknown): Record<string, string> => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as Record<string, string>;
      }
    } catch {
      // Not JSON: a single link.
    }
    return value.trim() ? { link: value } : {};
  }
  return Object.entries(value as Record<string, unknown>).reduce(
    (acc, [key, v]) => ({ ...acc, [key]: String(v) }),
    {} as Record<string, string>
  );
};

/**
 * Links as pasted often carry quotes around them (e.g. a path copied as
 * `"\\server\folder"`): drop surrounding whitespace and quotes before saving.
 */
export const cleanLinkRecord = (
  links: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(links).map(([key, value]) => [
      key,
      value.trim().replace(/^["'“”‘’«»`]+|["'“”‘’«»`]+$/g, "").trim(),
    ])
  );

type UtmCampaignSource = Pick<
  components["schemas"]["AssociatedCampaignResponse"],
  "briefingCampaignType" | "platform" | "name"
> & { goal?: { campaign?: string | null } | null };

/**
 * Automatic UTM of a destination. Mirrors the backend composition so the
 * preview matches what gets stored.
 */
export const useAutoUtmBuilder = (
  associatedCampaign?: UtmCampaignSource | null,
  briefing?: { campaignName?: string | null; name?: string | null } | null
) => {
  const context = useMemo(
    () => ({
      source: toUtmToken(
        associatedCampaign?.briefingCampaignType ?? "UNDEFINED"
      ),
      medium: toUtmToken(associatedCampaign?.platform ?? "platform"),
      campaign: toUtmToken(
        associatedCampaign?.goal?.campaign ||
          associatedCampaign?.name ||
          briefing?.campaignName ||
          briefing?.name ||
          "campaign"
      ),
    }),
    [
      associatedCampaign?.briefingCampaignType,
      associatedCampaign?.platform,
      associatedCampaign?.name,
      associatedCampaign?.goal?.campaign,
      briefing?.campaignName,
      briefing?.name,
    ]
  );

  return useCallback(
    (destination: DestinationDraft) => {
      const url = destination.url.trim();
      if (!url) return "";

      const separator = url.includes("?") ? "&" : "?";
      const query = [
        `utm_source=${context.source}`,
        `utm_medium=${context.medium}`,
        `utm_campaign=${context.campaign}`,
      ];
      if (destination.label.trim()) {
        query.push(`utm_content=${toUtmToken(destination.label)}`);
      }
      return `${url}${separator}${query.join("&")}`;
    },
    [context]
  );
};

/** Destinations as the API expects them (empty rows dropped). */
export const toDestinationPayload = (
  destinations: DestinationDraft[],
  buildAutoUtm: (destination: DestinationDraft) => string
) =>
  destinations
    .map((destination) => ({
      id: destination.id,
      label: destination.label.trim() || null,
      url: destination.url.trim() || null,
      utm:
        (destination.isAutoUtm
          ? buildAutoUtm(destination)
          : destination.utm.trim()) || null,
      isAutoUtm: destination.isAutoUtm,
    }))
    .filter((destination) => destination.label || destination.url);
